import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentArrowDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import 'styles/TimingDataEdit.css';
import { parseDate } from 'utils';
import { makeRequest } from 'services/request.service';
import ButtonComponent from 'components/common/ButtonComponent';
import TableComponent from 'components/common/TableComponent';
import TimingDataEditModal from 'components/modals/TimingDataEditModal';
import { useAuth } from 'auth/AuthContext';

export default function TimingDataEdit() {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user } = useAuth();
  const [state, setState] = useState({
    timingData: [],
    currentDate: parseDate(new Date()),
    searchTerm: '',
    isTimingDataModalOpen: false
  });

  const onTimingModalToogle = () =>
    setState(s => ({
      ...s,
      isTimingDataModalOpen: !state.isTimingDataModalOpen
    }));

  const getTimingData = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/data-edit/timing-data/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });

      setState({ ...state, timingData: response.data });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const postTimingData = async () => {
    try {
      const response = await makeRequest({
        method: 'POST',
        url: `/management/data-edit/timing-data/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState({ ...state, timingData: response.data });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filterTimingData = () => {
    return state.timingData.filter(
      timing =>
        timing.rowCode.toLowerCase().includes(state.searchTerm) ||
        timing.date.toLowerCase().includes(state.searchTerm) ||
        timing.time.toLowerCase().includes(state.searchTerm) ||
        timing.timingScanType.toLowerCase().includes(state.searchTerm) ||
        timing.workerName.toLowerCase().includes(state.searchTerm) ||
        timing.block.toLowerCase().includes(state.searchTerm) ||
        timing.activityName.toLowerCase().includes(state.search)
    );
  };

  // mocked data, replace with API
  const workersListMock = [
    { id: 1, name: 'Worker 1' },
    { id: 2, name: 'Worker 2' },
    { id: 3, name: 'Worker 3' },
    { id: 4, name: 'Worker 4' },
    { id: 5, name: 'Worker 5' }
  ];

  const timingTableData = {
    hascheckbox: true,
    columns: [
      { key: 'timingDataId', label: '#' },
      { key: 'rowCode', label: t('dataEdit.rowCode') },
      { key: 'date', label: t('dataEdit.date') },
      { key: 'time', label: t('dataEdit.time') },
      { key: 'timingScanType', label: t('dataEdit.scanType') },
      { key: 'workerName', label: t('dataEdit.worker') },
      { key: 'block', label: t('dataEdit.block') },
      { key: 'farmingBlockId', label: t('dataEdit.farmingBlockId') }, // map to workers name
      { key: 'activityName', label: t('dataEdit.activity') }
    ],
    rows: filterTimingData
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getTimingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 bg-white shadow-md border border-gray-300 border-t-0">
      <div className="section-header">
        <div className="header-left">
          <div className="date-filter">
            <span>
              {t('common.today')} ({state.currentDate})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 mb-6">
          <ButtonComponent onClick={onTimingModalToogle}>
            <PlusIcon className="w-5 h-5" />
            {t('dataEdit.createTimings')}
          </ButtonComponent>
          <ButtonComponent className="flex items-center gap-2">
            <DocumentArrowDownIcon className="w-5 h-5" />
            <span>{t('export.excel')}</span>
          </ButtonComponent>
          <ButtonComponent className="flex items-center gap-2">
            <DocumentArrowDownIcon className="w-5 h-5" />
            <span>{t('export.pdf')}</span>
          </ButtonComponent>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder={t('common.search')}
          value={state.searchTerm}
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
        />
      </div>

      {timingTableData.rows.length !== 0 && (
        <TableComponent
          tableData={timingTableData}
          defaultSortColumn={timingTableData.columns[0]?.key}
        />
      )}

      {state.isTimingDataModalOpen && (
        <TimingDataEditModal
          isOpen={state.isTimingDataModalOpen}
          onClose={onTimingModalToogle}
          onSave={postTimingData}
          initialData={state.timingData}
          workersList={workersListMock} // replace with API response
        />
      )}
    </div>
  );
}
